import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, OnChanges, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MailTemplatesService } from '../../../core/services/mail-templates/mail-templates.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { ALL_ROLES } from '../../../shared/roles';

@Component({
  selector: 'app-form-add-mail-template',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './form-add-mail-template.component.html',
  styleUrl: './form-add-mail-template.component.css',
})
export class FormAddMailTemplateComponent implements OnChanges, AfterViewInit {
  @Output() backButtonClick = new EventEmitter<boolean>();
  @Output() addTemplate = new EventEmitter<boolean>();
  @Input() templateData: any = null;
  @Input() logoUrl: string | null = null;

  @ViewChild('editorBody') editorBody!: ElementRef<HTMLDivElement>;
  @ViewChild('editorHeader') editorHeader!: ElementRef<HTMLDivElement>;
  @ViewChild('editorFooter') editorFooter!: ElementRef<HTMLDivElement>;
  @ViewChild('editorSignature') editorSignature!: ElementRef<HTMLDivElement>;

  form!: FormGroup;
  edit = false;
  reference: string | null = null;
  private editorsReady = false;

  availableRoles = ALL_ROLES;

  constructor(
    private fb: FormBuilder,
    private mailTemplatesService: MailTemplatesService,
    private alertService: AlertService,
  ) {
    this.initForm();
  }

  initForm(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      subject: [''],
      body_html: [''],
      logo_url: [''],
      primary_color: [''],
      secondary_color: [''],
      header_html: [''],
      footer_html: [''],
      signature_html: [''],
      roles: [[]],
    });
  }

  ngAfterViewInit(): void {
    this.editorsReady = true;
    this.syncEditorsFromForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateData'] && this.templateData) {
      this.edit = true;
      this.reference = this.templateData.reference;
      this.form.patchValue({
        name: this.templateData.name || '',
        subject: this.templateData.subject || '',
        body_html: this.templateData.body_html || '',
        logo_url: this.templateData.logo_url || '',
        primary_color: this.templateData.primary_color || '',
        secondary_color: this.templateData.secondary_color || '',
        header_html: this.templateData.header_html || '',
        footer_html: this.templateData.footer_html || '',
        signature_html: this.templateData.signature_html || '',
        roles: this.templateData.roles || [],
      });
      this.syncEditorsFromForm();
    } else if (changes['templateData'] && !this.templateData) {
      this.edit = false;
      this.reference = null;
      this.resetForm();
      this.syncEditorsFromForm();
    }
  }

  private syncEditorsFromForm(): void {
    if (!this.editorsReady) return;
    if (this.editorBody) this.editorBody.nativeElement.innerHTML = this.form.get('body_html')?.value || '';
    if (this.editorHeader) this.editorHeader.nativeElement.innerHTML = this.form.get('header_html')?.value || '';
    if (this.editorFooter) this.editorFooter.nativeElement.innerHTML = this.form.get('footer_html')?.value || '';
    if (this.editorSignature) this.editorSignature.nativeElement.innerHTML = this.form.get('signature_html')?.value || '';
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    this.alertService.loading('swal_messages.please_wait');

    const body = { ...this.form.value };
    if (this.logoUrl && !body.logo_url) {
      body.logo_url = this.logoUrl;
    }

    if (this.edit && this.reference) {
      this.mailTemplatesService.updateTemplate(this.reference, body).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.mail_templates.mail_template_updated');
          this.addTemplate.emit(true);
          this.emitBack();
        },
        error: (error) => this.alertService.error(error.error),
      });
    } else {
      this.mailTemplatesService.createTemplate(body).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.mail_templates.mail_template_created');
          this.addTemplate.emit(true);
          this.emitBack();
        },
        error: (error) => this.alertService.error(error.error),
      });
    }
  }

  resetForm(): void {
    this.form.reset({ name: '', subject: '', body_html: '', header_html: '', footer_html: '', signature_html: '', roles: [] });
    this.syncEditorsFromForm();
  }

  emitBack(): void {
    this.resetForm();
    this.backButtonClick.emit(true);
  }

  addRole(role: string): void {
    if (!role) return;
    const current: string[] = this.form.get('roles')?.value || [];
    if (!current.includes(role)) {
      this.form.get('roles')?.setValue([...current, role]);
    }
  }

  removeRole(role: string): void {
    const current: string[] = this.form.get('roles')?.value || [];
    this.form.get('roles')?.setValue(current.filter(r => r !== role));
  }

  getAvailableRolesFiltered(): string[] {
    const selected: string[] = this.form.get('roles')?.value || [];
    return this.availableRoles.filter(r => !selected.includes(r));
  }

  execCommand(command: string, value?: string): void {
    document.execCommand(command, false, value);
  }

  insertLink(): void {
    const url = prompt('URL del enlace:');
    if (url) {
      document.execCommand('createLink', false, url);
    }
  }

  onEditorInput(event: Event, field: string): void {
    const el = event.target as HTMLElement;
    this.form.get(field)?.setValue(el.innerHTML, { emitEvent: false });
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control?.invalid && control?.touched);
  }
}
